import { startApplication } from "../../application";
import type {
  PointerHandler,
  PointerInput,
  PointerSample,
} from "../../input/pointer";
import type { RuntimePlatform } from "../types";
import "./style.css";

function readProfile(): { coins: number; gems: number } {
  try {
    const saved = JSON.parse(
      localStorage.getItem("snowball-profile") ?? "{}",
    ) as { coins?: number; gems?: number };
    return {
      coins: Math.max(0, saved.coins ?? 0),
      gems: Math.max(0, saved.gems ?? 0),
    };
  } catch {
    return { coins: 0, gems: 0 };
  }
}

const canvas = document.querySelector<HTMLCanvasElement>("#game");
if (!canvas) throw new Error("Missing #game canvas");

// Create a probe element to read safe area insets
const probe = document.createElement("div");
probe.style.cssText = `
  position: fixed;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  padding-top: env(safe-area-inset-top);
  padding-right: env(safe-area-inset-right);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  pointer-events: none;
  opacity: 0;
`;
document.body.appendChild(probe);

function readSafeInsets() {
  const qa = new URLSearchParams(window.location.search);
  if (qa.get("qa") === "1" && qa.has("safe")) {
    const [top = 0, bottom = 0, left = 0, right = 0] = qa
      .get("safe")!
      .split(",")
      .map(Number);
    if ([top, bottom, left, right].every((v) => Number.isFinite(v) && v >= 0))
      return {
        top: Math.max(top, Number(qa.get("ttTop")) || 0),
        bottom,
        left,
        right,
      };
  }
  const computed = getComputedStyle(probe);
  return {
    top: parseFloat(computed.paddingTop) || 0,
    right: parseFloat(computed.paddingRight) || 0,
    bottom: parseFloat(computed.paddingBottom) || 0,
    left: parseFloat(computed.paddingLeft) || 0,
  };
}

type PointerKind = "start" | "move" | "end" | "cancel";
const handlers: Record<PointerKind, PointerHandler[]> = {
  start: [],
  move: [],
  end: [],
  cancel: [],
};
const sample = (event: PointerEvent): PointerSample => ({
  id: event.pointerId,
  x: event.clientX,
  y: event.clientY,
});

canvas.addEventListener("pointerdown", (event) => {
  if (event.isTrusted) canvas.setPointerCapture(event.pointerId);
  handlers.start.forEach((handler) => handler(sample(event)));
});
canvas.addEventListener("pointermove", (event) => {
  handlers.move.forEach((handler) => handler(sample(event)));
});
canvas.addEventListener("pointerup", (event) => {
  handlers.end.forEach((handler) => handler(sample(event)));
});
canvas.addEventListener("pointercancel", (event) => {
  handlers.cancel.forEach((handler) => handler(sample(event)));
});

const input: PointerInput = {
  onStart: (handler) => handlers.start.push(handler),
  onMove: (handler) => handlers.move.push(handler),
  onEnd: (handler) => handlers.end.push(handler),
  onCancel: (handler) => handlers.cancel.push(handler),
};
const uiCanvas = document.createElement("canvas");
const query = new URLSearchParams(window.location.search);
const safeInsets = readSafeInsets();
const platform: RuntimePlatform = {
  profile: readProfile(),
  reduceMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  canvas,
  uiCanvas,
  width: window.innerWidth,
  height: window.innerHeight,
  pixelRatio: window.devicePixelRatio,
  safeInsets,
  input,
  debug: query.get("debug") === "1",
  qa: query.get("qa") === "1",
  qaSeed:
    query.get("qa") === "1"
      ? Number(query.get("qaSeed") ?? query.get("seed") ?? 1234)
      : undefined,
  freezeTime: query.get("qaFreeze") === "1",
  qaSpeed:
    query.get("qa") === "1"
      ? Math.max(0.1, Math.min(10, Number(query.get("speed")) || 1))
      : 1,
  requestAnimationFrame: window.requestAnimationFrame.bind(window),
};

const application = startApplication(platform);
window.addEventListener("resize", () => {
  const newInsets = readSafeInsets();
  application.resize(
    window.innerWidth,
    window.innerHeight,
    window.devicePixelRatio,
    newInsets,
  );
});
